import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile, RedDocumento, FaseImmobile, TipologiaDocumentoUnificata } from '../../../types/red'
import { TIPOLOGIE_DOCUMENTO_LABEL, FASE_CONFIG, STATO_WORKFLOW_CONFIG } from '../../../constants/red'
import { redApi, tipologieDocumentoApi } from '../../../services/api'
import NuovaTipologiaDocumentoModal from '../../../components/red/NuovaTipologiaDocumentoModal'
import { downloadBlobResponse } from '../../../lib/downloadBlob'

const TabDocumenti: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const [documenti, setDocumenti] = useState<RedDocumento[]>([])
  const [loading, setLoading] = useState(true)
  const [uploadingId, setUploadingId] = useState<string | null>(null)
  const [showNuovo, setShowNuovo] = useState(false)
  const [nuovoTipologia, setNuovoTipologia] = useState('altro')
  const [nuovoTitolo, setNuovoTitolo] = useState('')
  const [creando, setCreando] = useState(false)
  const [tipologieDisponibili, setTipologieDisponibili] = useState<TipologiaDocumentoUnificata[]>([])
  const [showNuovaTipologia, setShowNuovaTipologia] = useState(false)
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set())
  const [downloadingZip, setDownloadingZip] = useState(false)

  useEffect(() => {
    tipologieDocumentoApi.listTutte()
      .then(res => setTipologieDisponibili(res.data))
      .catch(() => setTipologieDisponibili(
        Object.entries(TIPOLOGIE_DOCUMENTO_LABEL).map(([codice, nome]) => ({ codice, nome, fissa: true }))
      ))
  }, [])

  const loadDocumenti = () => {
    redApi.documenti.listByImmobile(immobile.id).then(res => {
      setDocumenti(res.data)
      setLoading(false)
    })
  }

  useEffect(loadDocumenti, [immobile.id])

  const handleCreaDocumento = async () => {
    if (!nuovoTitolo.trim()) {
      toast.error('Inserisci un titolo per il documento')
      return
    }
    setCreando(true)
    try {
      await redApi.documenti.createPlaceholder({
        immobile_id: immobile.id,
        fase: immobile.fase_corrente,
        tipologia: nuovoTipologia,
        titolo: nuovoTitolo.trim(),
      })
      toast.success('Documento aggiunto')
      setNuovoTitolo('')
      setNuovoTipologia('altro')
      setShowNuovo(false)
      loadDocumenti()
    } catch {
      toast.error('Errore creazione documento')
    } finally {
      setCreando(false)
    }
  }

  const handleUpload = async (documentoId: string, file: File) => {
    setUploadingId(documentoId)
    try {
      await redApi.documenti.upload(documentoId, file)
      toast.success('Documento caricato')
      loadDocumenti()
      onUpdate()
    } catch {
      toast.error('Errore upload')
    } finally {
      setUploadingId(null)
    }
  }

  const toggleSelezionato = (id: string) => setSelezionati(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const handleDownloadZip = async () => {
    setDownloadingZip(true)
    try {
      const res = await redApi.documenti.downloadZip([...selezionati])
      downloadBlobResponse(res, `${immobile.nome_riferimento || 'documenti'}.zip`)
    } catch {
      toast.error('Errore durante il download dello ZIP')
    } finally {
      setDownloadingZip(false)
    }
  }

  const handleDownload = async (doc: RedDocumento) => {
    try {
      const res = await redApi.documenti.download(doc.id)
      downloadBlobResponse(res, doc.titolo)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore durante il download del documento')
    }
  }

  const handleGeneraDomandaUsr = async () => {
    try {
      await redApi.documenti.generaDomandaUsr(immobile.id)
      toast.success('PDF domanda USR generato')
      loadDocumenti()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore generazione PDF')
    }
  }

  const handleRevisiona = async (docId: string, stato: string) => {
    try {
      await redApi.documenti.revisiona(docId, { stato_workflow: stato })
      toast.success(`Documento ${stato}`)
      loadDocumenti()
    } catch { toast.error('Errore') }
  }

  const handleVersiona = async (docId: string) => {
    try {
      await redApi.documenti.versiona(docId)
      toast.success('Nuova versione creata')
      loadDocumenti()
    } catch { toast.error('Errore') }
  }

  if (loading) return <div className="text-gray-400">Caricamento documenti...</div>

  const perFase = documenti.reduce<Record<string, RedDocumento[]>>((acc, d) => {
    (acc[d.fase] ||= []).push(d)
    return acc
  }, {})

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Documenti</h2>
        <div className="flex items-center gap-2">
          {immobile.fase_corrente === 'pratica_usr' && (
            <button onClick={handleGeneraDomandaUsr} className="btn-secondary text-sm">
              Genera PDF Domanda USR
            </button>
          )}
          <button
            onClick={handleDownloadZip}
            disabled={selezionati.size === 0 || downloadingZip}
            className="btn-secondary text-sm"
            title={selezionati.size === 0 ? 'Seleziona almeno un documento' : `Scarica ${selezionati.size} documenti selezionati in uno ZIP`}
          >
            {downloadingZip ? 'Download...' : `⬇️ Scarica${selezionati.size > 0 ? ` (${selezionati.size})` : ''}`}
          </button>
          <button onClick={() => setShowNuovo(v => !v)} className="btn-primary text-sm">
            + Aggiungi documento
          </button>
        </div>
      </div>

      {showNuovo && (
        <div className="card p-4 mb-4 flex items-end gap-3">
          <div className="flex-1">
            <label className="label">Tipologia</label>
            <select
              className="select"
              value={nuovoTipologia}
              onChange={e => {
                if (e.target.value === '__nuovo__') {
                  setShowNuovaTipologia(true)
                  return
                }
                setNuovoTipologia(e.target.value)
              }}
            >
              {tipologieDisponibili.map(t => (
                <option key={t.codice} value={t.codice}>{TIPOLOGIE_DOCUMENTO_LABEL[t.codice] || t.nome}</option>
              ))}
              <option value="__nuovo__">+ Nuovo tipo...</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="label">Titolo</label>
            <input type="text" className="input" value={nuovoTitolo} onChange={e => setNuovoTitolo(e.target.value)} placeholder="Es. Visura catastale aggiornata" />
          </div>
          <button onClick={handleCreaDocumento} disabled={creando} className="btn-primary text-sm">
            {creando ? 'Creazione...' : 'Crea'}
          </button>
        </div>
      )}

      {Object.entries(perFase).map(([fase, docs]) => {
        const cfg = FASE_CONFIG[fase as FaseImmobile]
        return (
          <div key={fase} className="mb-6">
            <h3 className="text-sm font-medium mb-2" style={{ color: cfg?.color }}>{cfg?.label || fase}</h3>
            <div className="space-y-2">
              {docs.map(doc => {
                const wf = STATO_WORKFLOW_CONFIG[doc.stato_workflow] || STATO_WORKFLOW_CONFIG.bozza
                return (
                  <div key={doc.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start gap-2 flex-1">
                      {doc.caricato && (
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selezionati.has(doc.id)}
                          onChange={() => toggleSelezionato(doc.id)}
                        />
                      )}
                      <div className="flex-1">
                      <div className="text-sm font-medium text-gray-800">
                        {doc.titolo || TIPOLOGIE_DOCUMENTO_LABEL[doc.tipologia] || tipologieDisponibili.find(t => t.codice === doc.tipologia)?.nome || doc.tipologia}
                        {doc.versione > 1 && <span className="text-xs text-gray-400 ml-1">v{doc.versione}</span>}
                      </div>
                      <div className="text-xs text-gray-400">
                        {doc.obbligatorio ? 'Obbligatorio' : 'Facoltativo'}
                        {doc.data_scadenza && ` · Scade: ${new Date(doc.data_scadenza).toLocaleDateString('it-IT')}`}
                        <span className="ml-2 px-1.5 py-0.5 rounded text-xs" style={{ color: wf.color, backgroundColor: wf.bg }}>{wf.label}</span>
                        {doc.numero_protocollo && (
                          <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700">📋 {doc.numero_protocollo}</span>
                        )}
                      </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.caricato ? (
                        <>
                          <span className="text-xs text-green-600 font-medium">✓ Caricato</span>
                          <button onClick={() => handleDownload(doc)} className="text-xs text-blue-600 hover:underline">Scarica</button>
                        </>
                      ) : (
                        <label className="text-xs text-blue-600 hover:underline cursor-pointer">
                          {uploadingId === doc.id ? 'Caricamento...' : 'Carica file'}
                          <input type="file" className="hidden" disabled={uploadingId === doc.id} onChange={e => { const file = e.target.files?.[0]; if (file) handleUpload(doc.id, file) }} />
                        </label>
                      )}
                      {/* Workflow buttons */}
                      {doc.stato_workflow === 'bozza' && (
                        <button onClick={() => handleRevisiona(doc.id, 'in_revisione')} className="text-xs text-amber-600 hover:underline">Invia rev.</button>
                      )}
                      {doc.stato_workflow === 'in_revisione' && (
                        <>
                          <button onClick={() => handleRevisiona(doc.id, 'approvato')} className="text-xs text-green-600 hover:underline">Approva</button>
                          <button onClick={() => handleRevisiona(doc.id, 'rifiutato')} className="text-xs text-red-600 hover:underline">Rifiuta</button>
                        </>
                      )}
                      {doc.stato_workflow === 'approvato' && (
                        <button onClick={() => handleVersiona(doc.id)} className="text-xs text-blue-600 hover:underline">Nuova ver.</button>
                      )}
                      {!doc.numero_protocollo && doc.caricato && (
                        <button onClick={async () => {
                          try { await redApi.documenti.protocollo(doc.id); toast.success('Documento protocollato'); loadDocumenti() } catch { toast.error('Errore protocollo') }
                        }} className="text-xs text-purple-600 hover:underline">Protocolla</button>
                      )}
                      <button onClick={async () => {
                        if (!confirm('Eliminare questo documento?')) return
                        try { await redApi.documenti.delete(doc.id); toast.success('Documento eliminato'); loadDocumenti(); onUpdate() } catch { toast.error('Errore eliminazione') }
                      }} className="text-xs text-red-500 hover:text-red-700 ml-1" title="Elimina">🗑</button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {documenti.length === 0 && (
        <div className="text-center py-10 text-gray-400">Nessun documento presente per questo immobile.</div>
      )}

      {showNuovaTipologia && (
        <NuovaTipologiaDocumentoModal
          onClose={() => setShowNuovaTipologia(false)}
          onCreated={tipo => {
            setTipologieDisponibili(prev => [...prev, { codice: tipo.codice, nome: tipo.nome, fissa: false }])
            setNuovoTipologia(tipo.codice)
          }}
        />
      )}
    </div>
  )
}

export default TabDocumenti
